/**
 * Hand-built SVG progress ring.
 * Fill = current / target (%). Auto-colors: green (on-track), amber (mid), red (at-risk).
 * Renders a nice tri-metric block: profit progress, days remaining, drawdown headroom.
 */
export default function ChallengeRing({
  progressPct = 0,     // 0-100+
  targetPct = 10,      // e.g. 10 for FTMO Phase 1
  drawdownPct = 0,
  maxDrawdownPct = 10,
  daysRemaining = null,
  size = 132,
  strokeWidth = 10,
  label = "Progress",
  status = "active",
  centerValue,
  centerSub,
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(1, progressPct / (targetPct || 1)));
  const offset = circumference * (1 - clamped);
  const ddHeadroom = Math.max(0, maxDrawdownPct - drawdownPct);
  const criticalDD = ddHeadroom <= 1.5;
  const passed = status === "passed";
  const failed = status === "failed";

  const strokeColor = failed
    ? "#ef4444"
    : passed
    ? "#f59e0b"          // gold on pass
    : criticalDD
    ? "#ef4444"
    : clamped >= 1
    ? "#10b981"
    : clamped >= 0.5
    ? "#3b82f6"
    : "#a78bfa";

  const displayPct = Math.round(progressPct * 10) / 10;
  const cx = size / 2;

  return (
    <div className="inline-flex flex-col items-center gap-1" data-testid="challenge-ring">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {/* Backdrop ring */}
        <circle
          cx={cx} cy={cx} r={radius}
          fill="none" stroke="#1f2937" strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={cx} cy={cx} r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${cx} ${cx})`}
          style={{ transition: "stroke-dashoffset 0.6s ease-out, stroke 0.3s" }}
        />
        {/* Target marker at 100% (12 o'clock) */}
        <circle
          cx={cx} cy={strokeWidth / 2}
          r={strokeWidth / 3}
          fill="#e5e7eb"
        />
        {/* Center text */}
        <text
          x={cx} y={cx - 4}
          textAnchor="middle"
          fontFamily="JetBrains Mono, monospace"
          fontSize={size / 6}
          fontWeight="800"
          fill={strokeColor}
        >
          {centerValue ?? `${displayPct >= 0 ? "+" : ""}${displayPct}%`}
        </text>
        <text
          x={cx} y={cx + size / 8}
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize={size / 14}
          fill="#9ca3af"
        >
          {centerSub ?? `of ${targetPct}% target`}
        </text>
      </svg>
      <div className="text-[10px] font-mono uppercase tracking-widest text-[#6b7280]">
        {label}
        {daysRemaining !== null && daysRemaining !== undefined && ` · ${daysRemaining}d left`}
      </div>
    </div>
  );
}
